import React, { useEffect, useState, useCallback } from "react";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { FaSkeletonKpis } from "../components/LoadingSkeleton";
import { fetchAuditLogs } from "../services/auditLogService";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function DashboardPage() {
  const { dashboardStats, loading, refreshDashboard } = useFirestoreAdmin();
  const [activity, setActivity] = useState([]);
  const [live, setLive] = useState(true);

  const loadActivity = useCallback(async () => {
    const logs = await fetchAuditLogs({ days: 1, max: 300 });
    const counts = {
      updates: 0,
      deletes: 0,
      restores: 0,
      imports: 0,
      exports: 0,
      backups: 0,
      errors: 0,
    };
    logs.forEach((l) => {
      const a = String(l.action || "");
      if (a.includes("delete")) counts.deletes += 1;
      else if (a.includes("restore")) counts.restores += 1;
      else if (a.includes("import")) counts.imports += 1;
      else if (a.includes("export")) counts.exports += 1;
      else if (a.includes("backup")) counts.backups += 1;
      else if (a.includes("error")) counts.errors += 1;
      else counts.updates += 1;
    });
    setActivity([
      { name: "Updates", value: counts.updates },
      { name: "Deletes", value: counts.deletes },
      { name: "Restores", value: counts.restores },
      { name: "Imports", value: counts.imports },
      { name: "Exports", value: counts.exports },
      { name: "Backups", value: counts.backups },
      { name: "Errors", value: counts.errors },
    ]);
  }, []);

  useEffect(() => {
    refreshDashboard();
    loadActivity();
  }, [refreshDashboard, loadActivity]);

  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => {
      refreshDashboard();
      loadActivity();
    }, 30000);
    return () => clearInterval(t);
  }, [live, refreshDashboard, loadActivity]);

  const cards = [
    ["Collections", dashboardStats.collections],
    ["Documents (sample)", dashboardStats.documents.toLocaleString()],
    ["Today's updates", dashboardStats.todayUpdates],
    ["Today's deletes", dashboardStats.todayDeletes],
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Activity Dashboard</p>
          <h1 className="text-2xl font-bold">Dashboard</h1>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> Live refresh (30s)
        </label>
      </header>
      {loading ? (
        <FaSkeletonKpis />
      ) : (
        <div className="fa-kpi-grid">
          {cards.map(([label, value]) => (
            <div key={label} className="fa-kpi">
              <p className="fa-kpi-label">{label}</p>
              <p className="fa-kpi-value">{value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="fa-card p-4">
        <h2 className="mb-4 font-semibold">Today&apos;s activity</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={activity}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#4f46e5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <button type="button" className="fa-btn fa-btn-primary" onClick={() => { refreshDashboard(); loadActivity(); }}>
        Refresh now
      </button>
    </div>
  );
}
